import { FC, useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import parse from 'html-react-parser';
import 'react-quill/dist/quill.snow.css';
import { Box, Typography } from '@mui/material';
import { useUpdateTaskDescriptionMutation } from 'redux/apiSlices/tasks.slice';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import EditNoteIcon from '@mui/icons-material/EditNote';

const TaskDescription: FC<{
  taskId: string;
  description?: string;
}> = ({ description: descriptionInitial, taskId }) => {
  const quillRef = useRef(null);

  const [viewMode, setViewMode] = useState(true);
  const [description, setDescription] = useState(descriptionInitial || '');
  const [updateDescription] = useUpdateTaskDescriptionMutation();

  const changeViewMode = () => {
    setViewMode(prevState => !prevState);
  };

  const onBlur = async () => {
    if (description.trim() !== descriptionInitial) {
      await updateDescription({ taskId, description: description.trim() });
    }
    changeViewMode();
  };

  useEffect(() => {
    if (quillRef.current) {
      // @ts-ignore
      quillRef.current.editor.focus();
    }
  });

  const hasDescription = description && description.trim() !== '' && description !== '<p><br></p>';

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          color: 'text.secondary',
          fontWeight: 600,
          mb: 2,
          textTransform: 'uppercase',
          fontSize: '0.7rem',
          letterSpacing: '0.5px',
        }}
      >
        Description
      </Typography>

      {viewMode ? (
        <Box
          onClick={changeViewMode}
          sx={{
            flex: 1,
            cursor: 'pointer',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            p: 2,
            overflow: 'auto',
            backgroundColor: 'background.paper',
            transition: 'border-color 0.2s, box-shadow 0.2s',
            '&:hover': {
              borderColor: 'primary.main',
              boxShadow: '0 0 0 1px rgba(29, 78, 216, 0.2)',
            },
            '& p': {
              margin: 0,
              marginBottom: 1,
            },
            '& ul, & ol': {
              marginTop: 0,
              marginBottom: 1,
              paddingLeft: 3,
            },
          }}
        >
          {hasDescription ? (
            parse(description)
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'text.secondary',
              }}
            >
              <EditNoteIcon fontSize="small" />
              <Typography variant="body2">
                Click to add a description...
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <ClickAwayListener onClickAway={onBlur}>
          <Box
            sx={{
              flex: 1,
              '& .quill': {
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              },
              '& .ql-toolbar': {
                borderRadius: '8px 8px 0 0',
                borderColor: 'divider',
                backgroundColor: 'background.default',
              },
              '& .ql-container': {
                borderRadius: '0 0 8px 8px',
                borderColor: 'divider',
                flex: 1,
                overflow: 'auto',
              },
              '& .ql-editor': {
                minHeight: 200,
              },
            }}
          >
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={description}
              onChange={setDescription}
              style={{ height: '100%' }}
              modules={{
                toolbar: [
                  [{ header: 1 }, { header: 2 }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
                  ['link', 'blockquote', 'code-block'],
                  ['clean'],
                ],
              }}
              preserveWhitespace={false}
            />
          </Box>
        </ClickAwayListener>
      )}
    </Box>
  );
};

export default TaskDescription;
